
import { useEffect, useState, useRef } from "react";
import { X, Plus, Trash2, Loader2, ChevronDown, Upload, ImageIcon } from "lucide-react";
import { Edicao, EdicaoForm } from "@/types/edicao";
import { criarEdicao, atualizarEdicao, uploadImagemEdicao } from "@/lib/edicoes";

const UFS = [
  "AC","AL","AP","AM","BA","CE","DF","ES","GO","MA",
  "MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN",
  "RS","RO","RR","SC","SP","SE","TO",
];

const EMPTY: EdicaoForm = {
  cidade: "",
  uf: "",
  nomeLocal: "",
  data: "",
  listaEspera: false,
  limiteListaEspera: null,
  atracoes: [""],
  linkIngressos: "",
  linkMaps: "",
  fotoAtracoes: "",
  fotoMapa: "",
};

interface Props {
  edicao?: Edicao | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function EdicaoModal({ edicao, onClose, onSaved }: Props) {
  const isEdit = !!edicao;
  const [form, setForm] = useState<EdicaoForm>(EMPTY);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");

  // Upload de fotos
  const [fileAtracoes, setFileAtracoes] = useState<File | null>(null);
  const [previewAtracoes, setPreviewAtracoes] = useState("");
  const [fileMapa, setFileMapa] = useState<File | null>(null);
  const [previewMapa, setPreviewMapa] = useState("");
  const refAtracoes = useRef<HTMLInputElement>(null);
  const refMapa = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (edicao) {
      setForm({
        cidade: edicao.cidade,
        uf: edicao.uf,
        nomeLocal: edicao.nomeLocal,
        data: edicao.data,
        listaEspera: edicao.listaEspera,
        limiteListaEspera: edicao.limiteListaEspera ?? null,
        atracoes: edicao.atracoes.length > 0 ? edicao.atracoes : [""],
        linkIngressos: edicao.linkIngressos ?? "",
        linkMaps: edicao.linkMaps ?? "",
        fotoAtracoes: edicao.fotoAtracoes ?? "",
        fotoMapa: edicao.fotoMapa ?? "",
      });
      setPreviewAtracoes(edicao.fotoAtracoes ?? "");
      setPreviewMapa(edicao.fotoMapa ?? "");
    } else {
      setForm(EMPTY);
      setPreviewAtracoes("");
      setPreviewMapa("");
      setFileAtracoes(null);
      setFileMapa(null);
    }
  }, [edicao]);

  const set = <K extends keyof EdicaoForm>(key: K, value: EdicaoForm[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const setAtracao = (index: number, value: string) =>
    setForm((f) => {
      const atracoes = [...f.atracoes];
      atracoes[index] = value;
      return { ...f, atracoes };
    });

  const addAtracao = () =>
    setForm((f) => ({ ...f, atracoes: [...f.atracoes, ""] }));

  const removeAtracao = (index: number) =>
    setForm((f) => ({
      ...f,
      atracoes: f.atracoes.filter((_, i) => i !== index),
    }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro("");

    const atracoesFiltradas = form.atracoes.filter((a) => a.trim() !== "");
    if (atracoesFiltradas.length === 0) {
      setErro("Adicione ao menos uma atração.");
      return;
    }

    const payload: EdicaoForm = {
      ...form,
      atracoes: atracoesFiltradas,
      limiteListaEspera: form.listaEspera ? (form.limiteListaEspera ?? null) : null,
      linkIngressos: form.linkIngressos?.trim() || "",
      linkMaps: form.linkMaps?.trim() || "",
      fotoAtracoes: form.fotoAtracoes || "",
      fotoMapa: form.fotoMapa || "",
    };

    setLoading(true);
    try {
      // Cria/atualiza primeiro para ter o ID
      let edicaoId = edicao?.id ?? "";
      if (isEdit && edicao) {
        await atualizarEdicao(edicao.id, payload);
      } else {
        edicaoId = await criarEdicao(payload);
      }

      // Upload das fotos se houver arquivo novo
      if (fileAtracoes) {
        const url = await uploadImagemEdicao(fileAtracoes, edicaoId, "atracoes");
        await atualizarEdicao(edicaoId, { ...payload, fotoAtracoes: url });
      }
      if (fileMapa) {
        const url = await uploadImagemEdicao(fileMapa, edicaoId, "mapa");
        await atualizarEdicao(edicaoId, { ...payload, fotoMapa: url });
      }

      onSaved();
    } catch {
      setErro("Ocorreu um erro ao salvar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-black/5">
          <h2 className="text-base font-semibold text-black">
            {isEdit ? "Editar Edição" : "Nova Edição"}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-black/30 hover:text-black hover:bg-black/5 transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <form
          id="form-edicao"
          onSubmit={handleSubmit}
          className="overflow-y-auto px-6 py-5 space-y-5"
        >
          {/* Cidade + UF */}
          <div className="flex gap-3">
            <div className="flex-1 space-y-1.5">
              <label className="text-xs font-medium text-black/60">Cidade</label>
              <input
                required
                type="text"
                placeholder="São Paulo"
                value={form.cidade}
                onChange={(e) => set("cidade", e.target.value)}
                className={inputCls}
              />
            </div>
            <div className="w-28 space-y-1.5">
              <label className="text-xs font-medium text-black/60">UF</label>
              <div className="relative">
                <select
                  required
                  value={form.uf}
                  onChange={(e) => set("uf", e.target.value)}
                  className={selectCls}
                >
                  <option value="">—</option>
                  {UFS.map((uf) => (
                    <option key={uf} value={uf}>{uf}</option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-black/30" />
              </div>
            </div>
          </div>

          {/* Nome do Local */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-black/60">Nome do Local</label>
            <input
              required
              type="text"
              placeholder="Arena XYZ"
              value={form.nomeLocal}
              onChange={(e) => set("nomeLocal", e.target.value)}
              className={inputCls}
            />
          </div>

          {/* Data */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-black/60">Data</label>
            <input
              required
              type="date"
              value={form.data}
              onChange={(e) => set("data", e.target.value)}
              className={inputCls}
            />
          </div>

          {/* Lista de espera */}
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => set("listaEspera", !form.listaEspera)}
              className="flex items-center gap-3 w-full text-left"
            >
              <div
                className={`w-10 h-6 rounded-full transition-colors ${
                  form.listaEspera ? "bg-black" : "bg-black/10"
                } relative shrink-0`}
              >
                <div
                  className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                    form.listaEspera ? "translate-x-5" : "translate-x-1"
                  }`}
                />
              </div>
              <span className="text-sm font-medium text-black">
                Haverá lista de espera?
              </span>
            </button>

            {form.listaEspera && (
              <div className="space-y-1.5 pl-13">
                <label className="text-xs font-medium text-black/60">
                  Limite da lista{" "}
                  <span className="text-black/30">(opcional)</span>
                </label>
                <input
                  type="number"
                  min={1}
                  placeholder="Sem limite"
                  value={form.limiteListaEspera ?? ""}
                  onChange={(e) =>
                    set(
                      "limiteListaEspera",
                      e.target.value ? Number(e.target.value) : null
                    )
                  }
                  className={inputCls}
                />
              </div>
            )}
          </div>

          {/* Atrações */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-black/60">Atrações</label>
            <div className="space-y-2">
              {form.atracoes.map((atracao, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    type="text"
                    placeholder={`Atração ${i + 1}`}
                    value={atracao}
                    onChange={(e) => setAtracao(i, e.target.value)}
                    className={`${inputCls} flex-1`}
                  />
                  {form.atracoes.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeAtracao(i)}
                      className="w-10 h-10 flex items-center justify-center rounded-xl text-black/30 hover:text-red-500 hover:bg-red-50 transition shrink-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addAtracao}
              className="flex items-center gap-1.5 text-xs font-medium text-black/40 hover:text-black transition mt-1"
            >
              <Plus className="w-3.5 h-3.5" />
              Adicionar atração
            </button>
          </div>

          {/* Link de Ingressos */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-black/60">
              Link de Ingressos{" "}
              <span className="text-black/30">(opcional)</span>
            </label>
            <input
              type="url"
              placeholder="https://..."
              value={form.linkIngressos}
              onChange={(e) => set("linkIngressos", e.target.value)}
              className={inputCls}
            />
          </div>

          {/* Link do Maps */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-black/60">
              Link do Google Maps{" "}
              <span className="text-black/30">(opcional)</span>
            </label>
            <input
              type="url"
              placeholder="https://maps.google.com/..."
              value={form.linkMaps}
              onChange={(e) => set("linkMaps", e.target.value)}
              className={inputCls}
            />
          </div>

          {/* Fotos */}
          <div className="grid grid-cols-2 gap-4">
            {(["atracoes", "mapa"] as const).map((campo) => {
              const label = campo === "atracoes" ? "Foto das Atrações" : "Foto do Mapa";
              const preview = campo === "atracoes" ? previewAtracoes : previewMapa;
              const fileRef = campo === "atracoes" ? refAtracoes : refMapa;
              const setPreview = campo === "atracoes" ? setPreviewAtracoes : setPreviewMapa;
              const setFile = campo === "atracoes" ? setFileAtracoes : setFileMapa;
              return (
                <div key={campo} className="space-y-1.5">
                  <label className="text-xs font-medium text-black/60">
                    {label} <span className="text-black/30">(opcional)</span>
                  </label>
                  <div
                    onClick={() => fileRef.current?.click()}
                    className="relative border-2 border-dashed border-black/10 rounded-xl overflow-hidden cursor-pointer hover:border-black/20 transition aspect-video flex items-center justify-center bg-black/[0.02]"
                  >
                    {preview ? (
                      <img src={preview} alt={label} className="w-full h-full object-cover" />
                    ) : (
                      <div className="flex flex-col items-center gap-1.5 text-black/20 py-4">
                        <ImageIcon className="w-6 h-6" />
                        <span className="text-xs">Selecionar</span>
                      </div>
                    )}
                    {preview && (
                      <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition flex items-center justify-center">
                        <Upload className="w-5 h-5 text-white" />
                      </div>
                    )}
                  </div>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      setFile(f);
                      setPreview(URL.createObjectURL(f));
                    }}
                  />
                </div>
              );
            })}
          </div>

          {/* Erro */}
          {erro && (
            <p className="text-sm text-red-500 bg-red-50 border border-red-100 px-4 py-3 rounded-xl">
              {erro}
            </p>
          )}
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-black/5">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl text-sm font-medium text-black/50 hover:text-black hover:bg-black/5 transition"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="form-edicao"
            disabled={loading}
            className="px-5 py-2.5 bg-black text-white text-sm font-medium rounded-xl hover:bg-black/80 transition disabled:opacity-50 flex items-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Salvando...
              </>
            ) : isEdit ? (
              "Salvar alterações"
            ) : (
              "Criar edição"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

const inputCls =
  "w-full px-3.5 py-2.5 rounded-xl border border-black/10 bg-black/[0.02] text-sm text-black placeholder-black/25 outline-none focus:border-black focus:bg-white transition";

const selectCls =
  "w-full pl-3.5 pr-9 py-2.5 rounded-xl border border-black/10 bg-black/[0.02] text-sm text-black outline-none focus:border-black focus:bg-white transition cursor-pointer appearance-none";
